// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — ENTITY-CREATION domain (CC-2c sub-arm 1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors blockDispatcher.ts for the entity-creation family. Each AI command
// shape is translated to the corresponding `EditorCommand` contract variant and
// returned for the caller to dispatch through the SAME store pipeline the UI /
// command line use (validation → audit → undo). Geometry is produced ONLY via
// these Command-Engine EditorCommands — this module NEVER writes an entity
// directly (CLAUDE.md: "AI ... может только предлагать и исполнять команды через
// безопасный контур").
//
// Safe-contour gate (CC-2a): `translateEntityCommand` runs the fail-closed
// `assertAiCallableEntityAction` guard BEFORE producing any EditorCommand, so a
// proposal whose target registry command is not `aiCallable: true` (or is
// absent from the registry) is rejected before anything reaches the store.
//
// The thin per-action translators live in the PACKAGE-INTERNAL sibling
// `entityTranslators.ts` (NOT re-exported by the barrel), so the ONLY public
// entity-creation entries are the guarded `translateEntityCommand` /
// `translateEntityCommandBatch` below. This mirrors the BLOCK domain (per-action
// translators in non-barrelled `blockDispatcher*.ts`) and the DRAW-MODIFY domain
// (`drawModifyTranslators.ts`), preventing a consumer of
// `@dxf-ai-editor/ai-commands` from emitting a geometry command past the
// fail-closed guard.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import { type AiDispatchContext } from "./blockDispatcherShared";
import { assertAiCallableEntityAction } from "./aiCallableGuard";
import type { EntityAiCommand } from "./entitySchemas";
import {
  translateCreateDimension,
  translateCreateHatch,
  translateCreateMtext,
  translateCreateMultileader,
  translateCreateRevcloud,
  translateCreateText,
} from "./entityTranslators";

// Re-export the entity safe-contour guard (mirrors blockDispatcher re-exporting
// the block guard) so consumers can reach it from this domain entry point.
export {
  AI_ENTITY_ACTION_TARGET_COMMAND,
  assertAiCallableEntityAction,
} from "./aiCallableGuard";

/**
 * Top-level entity-creation dispatcher. Runs the CC-2a fail-closed guard, then
 * routes to the per-command translator that owns the action. `descriptors` is
 * injectable for tests / alternate registries.
 */
export function translateEntityCommand(
  command: EntityAiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Safe-contour gate (CC-2a): refuse to translate any AI action whose target
  // command is not `aiCallable: true`. Fails closed before any EditorCommand.
  assertAiCallableEntityAction(command.action, descriptors);
  switch (command.action) {
    case "CREATE_TEXT":
      return translateCreateText(command, context);
    case "CREATE_MTEXT":
      return translateCreateMtext(command, context);
    case "CREATE_HATCH":
      return translateCreateHatch(command, context);
    case "CREATE_DIMENSION":
      return translateCreateDimension(command, context);
    case "CREATE_MLEADER":
      return translateCreateMultileader(command, context);
    case "CREATE_REVCLOUD":
      return translateCreateRevcloud(command, context);
  }
}

/**
 * Batch variant — flatten-translates a list of entity AI commands in order.
 */
export function translateEntityCommandBatch(
  commands: readonly EntityAiCommand[],
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  const all: EditorCommand[] = [];
  for (const command of commands) {
    all.push(...translateEntityCommand(command, context, descriptors));
  }
  return all;
}
